import React from 'react';
import { Alert, Box, Button, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from '../../Shared/Components/DataTable';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { InvoicesRoutes } from '../../../Router/path';
import { formatBookingCurrency } from '../Helpers/formatBookingCurrency';
import { getBookingReservationDisplayPartyId } from '../Helpers/getBookingReservationApplicantDisplay';
import { useBookingReservationTable } from '../Hooks/useBookingReservationTable';
import { useSearchableBookingReservationPartiesLookupQuery } from '../Service/bookingReservationPartiesLookupApi';
import { useDeleteBookingReservationMutation } from '../Service/bookingReservationsApi';
import type { BookingReservationRow } from '../Types/bookingReservationTable.types';
import { createBookingReservationColumns } from './BookingReservationColumns';
import { filterBookingReservations } from '../Helpers/filterBookingReservations';

interface BookingReservationDataTableProps {
  data: BookingReservationRow[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  totalCount: number;
  highlightedReservationId?: number | null;
  applicantDisplayMode?: 'split' | 'summary';
  showPaymentSummary?: boolean;
  showCreateInvoiceAction?: boolean;
  onRowDeleted?: () => void | Promise<void>;
  onSelectionChange?: (ids: number[]) => void;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (newPage: number) => void;
  onRowsPerPageChange?: (newRows: number) => void;
}

const BookingReservationDataTable: React.FC<BookingReservationDataTableProps> = ({
  data,
  isLoading,
  isError,
  errorMessage,
  totalCount,
  highlightedReservationId,
  applicantDisplayMode = 'split',
  showPaymentSummary = false,
  showCreateInvoiceAction = false,
  onRowDeleted,
  onSelectionChange,
  page: propPage,
  rowsPerPage: propRowsPerPage,
  onPageChange: propOnPageChange,
  onRowsPerPageChange: propOnRowsPerPageChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteReservation, { isLoading: isDeletingReservation }] =
    useDeleteBookingReservationMutation();
  const [deleteError, setDeleteError] = React.useState<string>('');
  const [applicantFilter, setApplicantFilter] = React.useState('');
  const [participantFilter, setParticipantFilter] = React.useState('');

  const { selectedReservationIds, setSelectedReservationIds, handleEdit } = useBookingReservationTable();
  const reservationDisplayPartyIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          data
            .map((reservation) => getBookingReservationDisplayPartyId(reservation))
            .filter((id): id is number => typeof id === 'number')
        )
      ),
    [data]
  );
  const { parties } = useSearchableBookingReservationPartiesLookupQuery(
    applicantDisplayMode === 'summary' && reservationDisplayPartyIds.length > 0
      ? { ids: reservationDisplayPartyIds, take: reservationDisplayPartyIds.length }
      : undefined
  );
  const columns = React.useMemo(
    () => createBookingReservationColumns({ parties }),
    [parties]
  );

  const filteredData = React.useMemo(
    () => filterBookingReservations(data, { applicantFilter, participantFilter }, parties),
    [data, applicantFilter, participantFilter, parties]
  );

  const handleApplicantFilterChange = (v: string) => {
    setApplicantFilter(v);
    propOnPageChange?.(0);
  };
  const handleParticipantFilterChange = (v: string) => {
    setParticipantFilter(v);
    propOnPageChange?.(0);
  };

  const handleSelectionChange = (ids: number[]) => {
    setSelectedReservationIds(ids);
    onSelectionChange?.(ids);
  };

  const isRowSelectable = (row: BookingReservationRow) => row.status !== 'cancelled';

  const getRowSx = (row: BookingReservationRow) =>
    row.status === 'cancelled' ? { opacity: 0.6, backgroundColor: 'action.hover' } : undefined;

  const handleDeleteRow = async (reservationId: number, bookingId: number) => {
    if (!window.confirm('Czy na pewno chcesz anulować tę rezerwację?')) {
      return;
    }

    try {
      setDeleteError('');
      await deleteReservation({ reservationId, bookingId }).unwrap();
      await onRowDeleted?.();
    } catch (error) {
      setDeleteError(extractApiErrorMessage(error, 'Nie udało się anulować rezerwacji.'));
    }
  };

  const handleCreateInvoice = (reservationId: number) => {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    navigate(
      `${InvoicesRoutes.create}?reservationId=${reservationId}&returnTo=${encodeURIComponent(returnTo)}`
    );
  };

  const paymentSummary = React.useMemo(() => {
    return data.reduce(
      (summary, reservation) => {
        const cashPaid = reservation.paidAmount ?? 0;
        const nonCashPaid = reservation.nonCashPaymentAmount ?? 0;
        const totalPaid = reservation.totalPaidAmount ?? cashPaid + nonCashPaid;
        const totalPrice = reservation.totalPrice ?? 0;
        const remainingToPay = Math.max(totalPrice - totalPaid, 0);
        const overpayment = Math.max(totalPaid - totalPrice, 0);

        return {
          cashPaid: summary.cashPaid + cashPaid,
          nonCashPaid: summary.nonCashPaid + nonCashPaid,
          totalPaid: summary.totalPaid + totalPaid,
          totalPrice: summary.totalPrice + totalPrice,
          remainingToPay: summary.remainingToPay + remainingToPay,
          overpayment: summary.overpayment + overpayment,
        };
      },
      {
        cashPaid: 0,
        nonCashPaid: 0,
        totalPaid: 0,
        totalPrice: 0,
        remainingToPay: 0,
        overpayment: 0,
      }
    );
  }, [data]);
  const isTableLoading = isLoading || isDeletingReservation;

  return (
    <>
      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteError}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Zgłaszający"
          size="small"
          value={applicantFilter}
          onChange={(e) => handleApplicantFilterChange(e.target.value)}
        />
        <TextField
          label="Uczestnik"
          size="small"
          value={participantFilter}
          onChange={(e) => handleParticipantFilterChange(e.target.value)}
        />
      </Box>
      <DataTable
        columns={columns}
        data={filteredData}
        tableLayout={applicantDisplayMode === 'summary' ? 'fixed' : 'auto'}
        isLoading={isTableLoading}
        isError={isError}
        errorMessage={errorMessage}
        getRowId={(row) => row.id}
        onEdit={(id) => handleEdit(id, data)}
        selected={selectedReservationIds}
        onSelectionChange={handleSelectionChange}
        page={propPage ?? 0}
        rowsPerPage={propRowsPerPage ?? 10}
        totalCount={filteredData.length}
        onPageChange={propOnPageChange}
        onRowsPerPageChange={propOnRowsPerPageChange}
        emptyMessage="Brak rezerwacji do wyświetlenia."
        highlightedRowId={highlightedReservationId}
        isRowSelectable={isRowSelectable}
        getRowSx={getRowSx}
        renderCustomActions={(row) => {
          if (row.status === 'cancelled') {
            // isRowSelectable also gates DataTable's own "Edytuj" action, so a cancelled row
            // needs its own view-only affordance here to stay reachable from the list.
            return (
              <Tooltip title="Podgląd">
                <span>
                  <IconButton
                    size="small"
                    color="default"
                    sx={{ p: 0.5 }}
                    onClick={() => handleEdit(row.id, data)}
                    aria-label="Podgląd"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            );
          }

          return (
            <>
              {showCreateInvoiceAction && (
                <Tooltip title="Wystaw FV">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      sx={{ p: 0.5 }}
                      disabled={isDeletingReservation}
                      onClick={() => handleCreateInvoice(row.id)}
                      aria-label="Wystaw FV"
                    >
                      <ReceiptLongIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Anuluj">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    sx={{ p: 0.5 }}
                    disabled={isDeletingReservation}
                    onClick={() => handleDeleteRow(row.id, row.bookingId)}
                    aria-label="Anuluj"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          );
        }}
      />

      {showPaymentSummary && !isTableLoading && !isError && data.length > 0 && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Podsumowanie wpłat:
          </Typography>
          <Chip label={`Gotówka: ${formatBookingCurrency(paymentSummary.cashPaid)}`} color="default" />
          <Chip
            label={`Bezgotówkowo: ${formatBookingCurrency(paymentSummary.nonCashPaid)}`}
            color="default"
          />
          <Chip label={`Łącznie: ${formatBookingCurrency(paymentSummary.totalPaid)}`} color="primary" />
          <Chip
            label={`Pozostało do zapłaty: ${formatBookingCurrency(paymentSummary.remainingToPay)}`}
            color="warning"
          />
          <Chip label={`Nadpłata: ${formatBookingCurrency(paymentSummary.overpayment)}`} color="info" />
        </Box>
      )}
    </>
  );
};

export default BookingReservationDataTable;
